// Static informational copy shown in the AttackBox launch modal.
// Kept separate from the modal so the wording can be edited without
// touching the modal's interactive logic (link, copy, open buttons).
import { Terminal, MessageCircle, ShieldAlert, BookOpen } from "lucide-react";
import PropTypes from "prop-types";

// Multi-VM labs don't ship with the CTF-Agent / Lab Assistant tooling, run on a
// trusted certificate, and have no Walkthroughs tab — so those three blocks are
// hidden for them via the `isMultiVm` flag.
export default function AttackBoxLaunchContent({ isMultiVm = false }) {
  return (
    <div className="space-y-4">
      <p className="text-xs leading-relaxed text-(--color-text-primary)">
        The AttackBox is your browser-based lab machine for playing this
        challenge. It ships with built-in tools and assistants to help you work
        through the exercise.
      </p>

      {/* Lab Assistants */}
      {!isMultiVm && (
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className="h-px flex-1 bg-linear-to-r from-transparent to-(--color-border)" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-(--color-text-muted)">
            Available Lab Assistants
          </span>
          <span className="h-px flex-1 bg-linear-to-l from-transparent to-(--color-border)" />
        </div>

        <div className="grid gap-2.5 sm:grid-cols-2">
          {/* CTF-Agent */}
          <div className="group relative rounded-lg border border-(--color-border) bg-(--color-bg)/40 p-3 transition-all hover:border-(--color-primary)/50 hover:shadow-[0_0_15px_rgba(0,255,0,0.12)]">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-(--color-primary)/10 border border-(--color-primary)/30 text-(--color-primary)">
                <Terminal className="h-4 w-4" />
              </div>
              <span className="text-xs font-bold text-(--color-text-primary)">
                CTF-Agent
              </span>
            </div>
            <p className="text-[11px] leading-relaxed text-(--color-text-primary)">
              Exploitation-focused support. Open a terminal and run{" "}
              <code className="font-mono text-[10px] bg-(--color-primary)/10 text-(--color-primary) border border-(--color-primary)/20 rounded px-1">
                ctf-agent
              </code>
              . Toggle between{" "}
              <span className="text-sky-400 font-semibold">blue</span> and{" "}
              <span className="text-red-400 font-semibold">red</span> team modes.
            </p>
          </div>

          {/* Lab Assistant */}
          <div className="group relative rounded-lg border border-(--color-border) bg-(--color-bg)/40 p-3 transition-all hover:border-(--color-primary)/50 hover:shadow-[0_0_15px_rgba(0,255,0,0.12)]">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-(--color-primary)/10 border border-(--color-primary)/30 text-(--color-primary)">
                <MessageCircle className="h-4 w-4" />
              </div>
              <span className="text-xs font-bold text-(--color-text-primary)">
                Lab Assistant
              </span>
            </div>
            <p className="text-[11px] leading-relaxed text-(--color-text-primary)">
              Click the green chat icon at the bottom-right. It knows the lab and
              scenario context — ask it when stuck, but it won't hand you the
              final answers.
            </p>
          </div>
        </div>
      </div>
      )}

      {/* Browser Security Warning */}
      {!isMultiVm && (
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
        <div className="flex items-center gap-2 mb-1.5">
          <ShieldAlert className="h-4 w-4 text-amber-400 shrink-0" />
          <span className="text-xs font-bold text-amber-300">
            Browser Security Warning
          </span>
        </div>
        <p className="text-[11px] leading-relaxed text-(--color-text-primary)">
          You may see “Your connection is not private.” This is expected — the
          AttackBox runs in an isolated lab environment without a public
          certificate. Click{" "}
          <strong className="text-(--color-text-primary)">Advanced</strong> →{" "}
          <strong className="text-(--color-text-primary)">Proceed</strong> to
          continue.
        </p>
      </div>
      )}

      {/* Need Instructions */}
      <div className="flex items-start gap-2.5 rounded-lg border border-(--color-border) bg-(--color-bg)/40 p-3">
        <BookOpen className="h-4 w-4 text-(--color-primary) shrink-0 mt-0.5" />
        <p className="text-[11px] leading-relaxed text-(--color-text-primary)">
          <span className="font-bold text-(--color-text-primary)">
            Need instructions?
          </span>{" "}
          Use the{" "}
          <strong className="text-(--color-text-primary)">Walkthroughs</strong>{" "}
          tab on the Box page for step-by-step guidance, hints, and challenge
          instructions.
        </p>
      </div>
    </div>
  );
}

AttackBoxLaunchContent.propTypes = {
  isMultiVm: PropTypes.bool,
};
